import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentType } from 'preact';
import { BadgeCheck, Gift, Play, Sparkles, Ticket, Trash2 } from 'lucide-preact';
import type {
  CoasterRuntime,
  RuntimeMediaState,
  RuntimeMonetizationState,
} from '../app/runtime-types.ts';
import type { MediaArtifactKind } from '../media/index.ts';
import { CloseButton, Disclosure, OverflowButton } from './primitives.tsx';
import { UI_COPY } from './ui-copy.ts';

type MediaTab = 'photo' | 'highlight' | 'card' | 'loop';

function tabArtifact(tab: MediaTab): MediaArtifactKind {
  if (tab === 'highlight') return 'highlight_video';
  if (tab === 'card') return 'share_card';
  if (tab === 'loop') return 'animated_loop';
  return 'photo';
}

export function MediaStudio({
  state,
  runtime,
  title,
  ReactionOptions,
  monetization,
}: {
  readonly state: RuntimeMediaState;
  readonly runtime: CoasterRuntime;
  readonly title: string;
  readonly monetization: RuntimeMonetizationState;
  readonly ReactionOptions?: ComponentType<{
    state: RuntimeMediaState;
    runtime: CoasterRuntime;
    busy: boolean;
    compact?: boolean;
  }>;
}) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const [tab, setTab] = useState<MediaTab>(() =>
    state.videoUrl ? 'highlight' : state.loopUrl ? 'loop' : 'photo',
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const artifact = tabArtifact(tab);
  const artifactReady =
    tab === 'photo'
      ? Boolean(state.photoUrl)
      : tab === 'card'
        ? Boolean(state.cardUrl)
        : tab === 'highlight'
          ? Boolean(state.videoUrl)
          : Boolean(state.loopUrl);
  const busy = state.status === 'preparing' || state.status === 'encoding';
  const estimatedSeconds = Math.max(1, Math.ceil((1 - state.progress) * (tab === 'loop' ? 5 : 18)));
  const selectedEditDescription =
    state.highlightMode === 'scenic'
      ? 'A cinematic coaster showcase uses three distinct track and park views.'
      : state.highlightMode === 'mixed'
        ? `${state.motionMomentCount} moving ride ${
            state.motionMomentCount === 1 ? 'moment' : 'moments'
          } plus scenic coaster views.`
        : state.selectedAlternative === 0
          ? 'Three diverse action moments in ride order.'
          : state.selectedAlternative === 1
            ? 'Drops, airtime, and speed take priority, with at least one inversion when available.'
            : state.selectedAlternative === 2
              ? 'The Best Moments sequence from the same first-person view used while riding.'
              : 'A deterministic mix using other safe ride moments.';
  const selectedOffer = monetization.offers.find(
    (offer) => offer.id === monetization.selectedOfferId,
  );
  const selectedOfferAccessible = Boolean(
    selectedOffer &&
    (monetization.passOwned || monetization.sessionGrants.includes(selectedOffer.id)),
  );
  const transactionBusy =
    monetization.transactionState === 'processing' || monetization.transactionState === 'checking';
  const visibleOffers = monetization.offers.filter(
    (offer) =>
      offer.id !== 'coaster_pass' ||
      monetization.passOwned ||
      monetization.premiumIntentCount >= 2 ||
      monetization.selectedOfferId === 'coaster_pass',
  );

  useEffect(() => {
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    dialog?.querySelector<HTMLElement>('button, [href], input, select, textarea')?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        runtime.closeMediaStudio();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [runtime]);

  return (
    <div class="modal-backdrop media-studio-backdrop" role="presentation">
      <section
        ref={dialogRef}
        class="media-studio"
        role="dialog"
        aria-modal="true"
        aria-labelledby="media-studio-title"
        data-testid="media-studio"
      >
        <header class="media-studio-header">
          <div>
            <h2 id="media-studio-title">{UI_COPY.media.title}</h2>
            <span class="media-studio-coaster-name">{title}</span>
          </div>
          <CloseButton label="Close Ride Media" onClick={() => runtime.closeMediaStudio()} />
        </header>

        <div class="media-tabs" role="tablist" aria-label="Media format">
          {(
            [
              ['photo', UI_COPY.media.photo],
              ['highlight', state.videoUrl ? 'Video · Ready' : 'Video'],
              ['card', UI_COPY.media.previewImage],
              ['loop', state.loopUrl ? 'Short Loop · Ready' : UI_COPY.media.shortLoop],
            ] as const
          ).map(([value, label]) => (
            <button
              type="button"
              role="tab"
              aria-selected={tab === value}
              class={tab === value ? 'active' : ''}
              onClick={() => setTab(value)}
            >
              {label}
            </button>
          ))}
        </div>

        <div class="media-preview" data-testid={`media-preview-${tab}`}>
          {tab === 'photo' && state.photoUrl && (
            <img src={state.photoUrl} alt={`Action photo from ${title}`} />
          )}
          {tab === 'card' && state.cardUrl && (
            <img src={state.cardUrl} alt={`Coaster overview for ${title}`} />
          )}
          {tab === 'highlight' && state.videoUrl && (
            <video src={state.videoUrl} controls playsInline preload="metadata" />
          )}
          {tab === 'loop' && state.loopUrl && (
            <video src={state.loopUrl} controls playsInline loop muted preload="metadata" />
          )}
          {!artifactReady && (
            <div class="media-placeholder">
              <strong>
                {busy
                  ? 'Working on it…'
                  : tab === 'highlight'
                    ? 'Ready to make your video'
                    : tab === 'loop'
                      ? 'Ready to make a loop'
                      : tab === 'photo'
                        ? 'Preparing Action Photo'
                        : 'Preparing Coaster Overview'}
              </strong>
              <span>
                {tab === 'highlight'
                  ? 'A 15-second vertical replay with your best moments.'
                  : tab === 'loop'
                    ? 'A silent eight-second orbit around your complete coaster.'
                    : tab === 'photo'
                      ? 'A portrait action image for social feeds.'
                      : 'A landscape overview for links and listings.'}
              </span>
            </div>
          )}
        </div>

        {(tab === 'photo' || tab === 'card') && (
          <p class="media-artifact-purpose">
            {tab === 'photo'
              ? 'Portrait action photo for sharing a memorable ride moment.'
              : 'Landscape overview showing the complete coaster for links and listings.'}
          </p>
        )}

        <div class="media-status" aria-live="polite">
          <span>{state.message}</span>
          {state.status === 'encoding' && (
            <>
              <progress max="1" value={state.progress} aria-label="Video generation progress" />
              <small>About {estimatedSeconds} seconds remaining on this device.</small>
            </>
          )}
          {state.timelineSource === 'restored' && (
            <small>Restored from your last completed ride.</small>
          )}
        </div>

        {tab === 'photo' && state.posterCandidates.length > 0 && (
          <div class="poster-choices" role="group" aria-label="Photo moment">
            {state.posterCandidates.map((candidate) => (
              <button
                type="button"
                class={state.selectedPosterId === candidate.id ? 'active' : ''}
                aria-pressed={state.selectedPosterId === candidate.id}
                disabled={busy}
                onClick={() => void runtime.selectPosterCandidate(candidate.id)}
              >
                {candidate.thumbnailUrl ? (
                  <img src={candidate.thumbnailUrl} alt="" aria-hidden="true" />
                ) : (
                  <span class="poster-thumb-placeholder" aria-hidden="true" />
                )}
                <span>{candidate.label}</span>
              </button>
            ))}
            {state.creatorPosterSkipped && (
              <small class="highlight-choice-description media-poster-note">
                The creator marker was too slow or too close to the station, so a stronger action
                moment was selected.
              </small>
            )}
          </div>
        )}

        {tab === 'highlight' && (
          <div class="media-options">
            <div class="highlight-choices" role="group" aria-label="Highlight edit">
              {([0, 1, 2, 3] as const)
                .filter((value) => value !== 3 || monetization.activeTreatments.freshHighlightMix)
                .map((value) => (
                  <button
                    type="button"
                    aria-pressed={state.selectedAlternative === value}
                    onClick={() => runtime.selectHighlightAlternative(value)}
                    disabled={busy}
                  >
                    {state.editLabels[value]}
                  </button>
                ))}
            </div>
            <small class="highlight-choice-description">
              {selectedEditDescription} Generated versions stay ready while this coaster remains
              open.
            </small>
            <label class="toggle-row">
              <input
                type="checkbox"
                checked={state.includeAudio}
                onChange={(event) => runtime.setMediaAudioEnabled(event.currentTarget.checked)}
                disabled={busy}
              />
              <span>Include ride sound</span>
            </label>
            {state.reactionRawUrl && ReactionOptions && (
              <ReactionOptions state={state} runtime={runtime} busy={busy} />
            )}
          </div>
        )}

        {monetization.sandbox && (
          <details class="premium-styles">
            <summary>
              <span class="premium-styles-icon" aria-hidden="true">
                <Sparkles size={18} />
              </span>
              <div>
                <strong>More Styles</strong>
                <small>Optional QA sandbox extras. Every current media format stays free.</small>
              </div>
              <span class="qa-balance">
                {monetization.availability.state === 'available'
                  ? `${monetization.wallet?.balance ?? 0} Credits`
                  : 'Offline'}
              </span>
            </summary>

            {monetization.availability.state !== 'available' ? (
              <div class="premium-offer-detail">
                <strong>Optional styles are offline right now</strong>
                <p>{monetization.availability.reason}</p>
                <p>Your free media and Download are still ready.</p>
                <button
                  class="tertiary-action"
                  type="button"
                  onClick={() => runtime.selectPremiumOffer(null)}
                >
                  Keep Free Version
                </button>
              </div>
            ) : (
              <div class="premium-offer-list" role="list" aria-label="Optional styles">
                {visibleOffers.map((offer) => {
                  const granted =
                    monetization.passOwned || monetization.sessionGrants.includes(offer.id);
                  const selected = monetization.selectedOfferId === offer.id;
                  return (
                    <button
                      type="button"
                      class={selected ? 'selected' : ''}
                      aria-pressed={selected}
                      disabled={transactionBusy}
                      onClick={() => runtime.selectPremiumOffer(selected ? null : offer.id)}
                    >
                      {offer.id === 'coaster_pass' ? (
                        <Ticket aria-hidden="true" size={18} />
                      ) : granted ? (
                        <BadgeCheck aria-hidden="true" size={18} />
                      ) : (
                        <Gift aria-hidden="true" size={18} />
                      )}
                      <span>
                        <strong>{offer.title}</strong>
                        <small>
                          {offer.id === 'coaster_pass'
                            ? `${offer.priceCredits} Credits · restores in this QA profile`
                            : granted
                              ? 'Ready while this coaster stays open'
                              : `${offer.priceCredits} Credits${offer.rewardedAdAvailable ? ' or optional ad' : ''}`}
                        </small>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {selectedOffer && (
              <div class="premium-offer-detail">
                <strong>{selectedOffer.title}</strong>
                <p>{selectedOffer.description}</p>
                {selectedOffer.id === 'passenger_cam' && !monetization.passengerCamEligible && (
                  <small>
                    Complete a Reaction Cam ride first. The free corner pane is unchanged.
                  </small>
                )}
                <div class="premium-offer-actions">
                  {selectedOffer.id === 'coaster_pass' && monetization.passOwned ? (
                    <button class="secondary-action" type="button" disabled>
                      Pass Active
                    </button>
                  ) : selectedOfferAccessible ? (
                    <button
                      class="primary-action"
                      type="button"
                      disabled={
                        transactionBusy ||
                        (selectedOffer.id === 'passenger_cam' && !monetization.passengerCamEligible)
                      }
                      onClick={() => void runtime.applyPremiumOffer(selectedOffer.id)}
                    >
                      Apply Style
                    </button>
                  ) : (
                    <>
                      <button
                        class="primary-action"
                        type="button"
                        disabled={
                          transactionBusy ||
                          (selectedOffer.id === 'passenger_cam' &&
                            !monetization.passengerCamEligible)
                        }
                        onClick={() =>
                          void runtime.acquirePremiumOffer(selectedOffer.id, 'credits')
                        }
                      >
                        Use {selectedOffer.priceCredits} Credits
                      </button>
                      {selectedOffer.rewardedAdAvailable && (
                        <button
                          class="secondary-action"
                          type="button"
                          disabled={transactionBusy}
                          onClick={() =>
                            void runtime.acquirePremiumOffer(selectedOffer.id, 'rewarded_ad')
                          }
                        >
                          Watch Optional Ad
                        </button>
                      )}
                    </>
                  )}
                  <button
                    class="tertiary-action"
                    type="button"
                    disabled={transactionBusy}
                    onClick={() => runtime.selectPremiumOffer(null)}
                  >
                    Keep Free Version
                  </button>
                </div>
                <small class="premium-session-note">
                  Standard extras last while this coaster remains open, including retries.
                </small>
              </div>
            )}

            <div class="premium-status" aria-live="polite">
              {transactionBusy && <span class="premium-spinner" aria-hidden="true" />}
              <span>{monetization.message}</span>
            </div>

            <Disclosure summary="QA sandbox controls">
              <div class="premium-qa-controls">
                <label>
                  Simulated result
                  <select
                    disabled={transactionBusy}
                    onChange={(event) =>
                      runtime.setMonetizationSandboxScenario(
                        event.currentTarget.value as
                          | 'success'
                          | 'insufficient_balance'
                          | 'cancelled'
                          | 'delayed_success'
                          | 'duplicate_response'
                          | 'offline'
                          | 'revoked',
                      )
                    }
                  >
                    <option value="success">Success</option>
                    <option value="insufficient_balance">Insufficient balance</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="delayed_success">Delayed success</option>
                    <option value="duplicate_response">Duplicate response</option>
                    <option value="offline">Offline</option>
                    <option value="revoked">Revoked</option>
                  </select>
                </label>
                <button
                  class="tertiary-action"
                  type="button"
                  disabled={transactionBusy}
                  onClick={() => void runtime.restorePremiumEntitlements()}
                >
                  Restore QA Purchases
                </button>
              </div>
            </Disclosure>
          </details>
        )}

        <div class="media-actions">
          {tab === 'highlight' && !state.videoUrl && (
            <button
              class="primary-action"
              type="button"
              disabled={busy || !state.formatLabel || !state.videoAvailable}
              onClick={() => void runtime.generateHighlightVideo()}
            >
              Generate Video{state.formatLabel ? ` (${state.formatLabel})` : ''}
            </button>
          )}
          {tab === 'loop' && !state.loopUrl && (
            <button
              class="primary-action"
              type="button"
              disabled={busy || !state.formatLabel || !state.videoAvailable}
              onClick={() => void runtime.generateAnimatedLoop()}
            >
              Generate Loop
            </button>
          )}
          {state.status === 'encoding' && (
            <button
              class="secondary-action"
              type="button"
              onClick={() => runtime.cancelMediaExport()}
            >
              Cancel
            </button>
          )}
          {artifactReady && (
            <>
              <button
                class="primary-action"
                type="button"
                onClick={() => runtime.downloadMedia(artifact)}
              >
                Download
              </button>
              {state.imageCopyAvailable && (tab === 'photo' || tab === 'card') && (
                <button
                  class="secondary-action"
                  type="button"
                  onClick={() => void runtime.copyMediaImage(artifact)}
                >
                  Copy Image
                </button>
              )}
            </>
          )}
          {state.publicLinkAvailable && (
            <button
              class="secondary-action"
              type="button"
              onClick={() => void runtime.copyMediaRideLink()}
            >
              Copy Ride Link
            </button>
          )}
          <span class="media-overflow">
            <OverflowButton
              label="More media actions"
              size="compact"
              type="button"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((open) => !open)}
            />
            {moreOpen && (
              <div class="bc-popover-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMoreOpen(false);
                    runtime.startReplay();
                  }}
                >
                  <Play aria-hidden="true" size={17} />
                  Watch Full Replay
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={busy}
                  onClick={() => {
                    setMoreOpen(false);
                    setConfirmDelete(true);
                  }}
                >
                  <Trash2 aria-hidden="true" size={17} />
                  Delete Local Media
                </button>
              </div>
            )}
          </span>
        </div>

        {confirmDelete && (
          <div class="media-delete-confirm" role="group" aria-label="Confirm media deletion">
            <strong>Delete local ride files?</strong>
            <span>
              This removes the replay and generated previews from this browser. It does not delete
              the coaster or downloaded files.
            </span>
            <button class="secondary-action" type="button" onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
            <button
              class="danger-action"
              type="button"
              onClick={() => void runtime.deleteRideMedia()}
            >
              Delete Local Media
            </button>
          </div>
        )}

        <Disclosure summary="Privacy & storage">
          <p class="media-privacy-note">
            Created on this device and never uploaded by Buncha Coasters. Generated videos and loops
            stay ready while this coaster remains open. Reaction Cam recordings are removed when you
            leave this coaster, reload, or close the tab.
            {!state.publicLinkAvailable && (
              <> Private links are never placed inside exported media.</>
            )}
          </p>
        </Disclosure>
        {monetization.cheerPulse > 0 && (
          <div class="premium-cheer" key={monetization.cheerPulse} aria-live="polite">
            <span aria-hidden="true">★</span>
            Cheer! Cosmetic only—no ranking effect.
          </div>
        )}
      </section>
    </div>
  );
}
